import { IconMenu2, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Z_INDEX } from "@/lib/z-index";
import type { UserInfo } from "@/lib/types";
import { NotificacionesCampana } from "@/features/notificaciones/components/NotificacionesCampana";
import { MultivaBrand } from "./MultivaBrand";
import { SearchTrigger } from "./SearchTrigger";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";

/**
 * Header del AppShell · 3 zonas (burger+brand / búsqueda central / acciones
 * a la derecha), gradiente naranja de marca. Al hacer scroll aparece una
 * sombra sutil (mismo micro-detalle que el original, vía un listener
 * pasivo de `scroll`).
 */
export function AppShellHeader({
  user,
  mobileNavOpen,
  onMobileToggle,
  onLogout,
}: {
  user: UserInfo | null;
  mobileNavOpen: boolean;
  onMobileToggle: () => void;
  onLogout: () => void;
}) {
  const scrolled = useScrollState(8);
  const roles = user?.roles ?? [];
  // Campana de solicitudes de catálogo (P38) · solo roles que pueden pedirlas
  // (los endpoints /catalogos/solicitudes/mias exigen estos roles).
  const conCampana = roles.some((r) => r === "Solicitante" || r === "MesaDeControl");

  return (
    <header
      className="fixed inset-x-0 top-0 flex h-[var(--shell-header-h)] items-center border-b border-border bg-card px-4 text-foreground transition-shadow duration-200"
      style={{
        zIndex: Z_INDEX.appHeader,
        boxShadow: scrolled ? "0 2px 8px -4px rgba(15,23,42,0.18)" : "none",
      }}
    >
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={onMobileToggle}
            aria-label={mobileNavOpen ? "Cerrar menú" : "Abrir menú"}
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted md:hidden"
          >
            {mobileNavOpen ? <IconX size={18} /> : <IconMenu2 size={18} />}
          </button>
          <MultivaBrand />
        </div>

        <SearchTrigger />

        <div className="flex shrink-0 items-center gap-1.5">
          {conCampana && <NotificacionesCampana />}
          <ThemeToggle />
          <UserMenu user={user} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
}

/** true cuando `window.scrollY > threshold` · listener pasivo, se limpia solo. */
function useScrollState(threshold: number): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    function handler() {
      setScrolled(window.scrollY > threshold);
    }
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [threshold]);
  return scrolled;
}
